import React, { FC, useMemo, useEffect,useState } from "react";
import dynamic from "next/dynamic";
import styled from "styled-components";
import * as Sentry from "@sentry/node";
import { ExhibitionProject, MonumentName, ProjectTotalization } from "interfaces/front/business";
import { calcLoginStatus, useFrontAuthContext } from "lib/hoc/withFrontAuth";
import { useAmount } from "lib/hooks/useAmount";
import { useMemberTippingComment } from "lib/hooks/useMemberTippingComment";
import { useRating } from "lib/hooks/useRating";
import { useRemainingDays } from "lib/hooks/useRemainingDays";
import { useScrollLimitation } from "lib/hooks/useScrollLimitation";
import { useSupportAmountPool } from "lib/hooks/useSupportAmountPool";
import { useDeviceType } from "lib/util";
import { useRouter } from "next/router";
import { useCourseListSwitch } from "lib/hooks/useCourseListSwitch";
import useShowsSupportAmount from "../../../lib/hooks/useShowsSupportAmount";
import { FIREWORK_FG_FRAME_ID } from "../aniale/ForegroundFireworkFrame";
import { SectionContent } from "./SectionContent";
import SsHeadline from "../layout/SsHeadline";
import useSmoothAnchor from "../../../lib/hooks/useSmoothAnchor";
import AnchorTarget from "components/front/layout/AnchorTarget";
import Section from "components/front/project/layout/Section";
import BannerArea from "components/front/project/layout/BannerArea";
import AnimefurusatoLpBanner from "components/front/project/banner/AnimefurusatoLpBanner";
import SimulatorBanner from "components/front/project/banner/SimulatorBanner";
import HometownTaxVideoBanner from "components/front/project/banner/HometownTaxVideoBanner";
import TitleSection from "./TitleSection";
import { LoadingDarkBg } from "../layout/DarkBackground";
import { LinkButtonSp } from "../Form/LinkButtonSp";
import SpBottomLeftCorner from "../custom/SpBottomLeftCorner";
import ProjectContentStyle from "components/front/project/ProjectContentStyle";
import { Header } from "components/front/layout/Header";
import { Props as QuantitativeSectionPcProps } from "./quantitative/QuantitativeSectionPc";
import { Props as QuantitativeSectionSpProps } from "./quantitative/QuantitativeSectionSp";
import { Props as MainImageSectionProps } from "components/front/project/MainImageSection";
import { Props as StartCountDownAreaProps } from "./StartCountDownArea";
import { Props as ZendeskHelpButtonProps } from "./ZendeskHelpButton";
import { Props as CourseListSectionProps } from "components/front/project/content/CourseListSection";
import { Props as RequestContactButtonProps } from "./contact/RequestContactButton";
import { Props as CommentSectionProps } from "./comment/CommentSection";
import { Props as StoneMonumentSectionProps } from "components/front/project/StoneMonumentSection";
import { Props as CorporateLogoSectionProps } from "components/front/project/CorporateLogoSection";
import { Props as RelatedLinkSectionProps } from "components/front/project/RelatedLinkSection";
import { Props as CourseListViewProps } from "components/front/project/CourseListView";
import { Props as PopupProps } from "./Popup";
import { Props as BackToTopProps } from "components/front/layout/BackToTop";
import { Props as FooterProps } from "components/front/layout/Footer";
import { Props as GeneralFootProps } from "../layout/GeneralFoot";
import { Props as HometownTaxAnnotationProps } from "./HometownTaxAnnotation";
import { Props as AmagamiTextProps } from "./AmagamiText";
import { Props as KaleidoShoppingTextProps } from "./KaleidoShoppingText";
import { Props as DetailQAListProps } from "./DetailQAList";
import { Props as ProjectActivitySectionProps } from "components/front/project/activity/ProjectActivitySection";
import { Props as HometownTaxVideoPopupProps } from "components/front/project/banner/HometownTaxVideoPopup";
import { Aniale, DonationCourse, RewardCourse } from "interfaces/front/page/project";
import { useActivation } from "lib/hooks/useActivation";
import useContentAccordion from "lib/hooks/useContentAccordion";
import ProjectBanner from "components/front/project/banner/ProjectBanner";
import useReturnIntroLabel from "lib/hooks/useReturnIntroLabel";
import useServiceType from "lib/hooks/front/useServiceType";
import CorpfurusatoBanner from "components/front/project/banner/CorpfurusatoBanner";

const MainImageSection = dynamic<MainImageSectionProps>(() => import("components/front/project/MainImageSection"));
const QuantitativeSectionSp = dynamic<QuantitativeSectionSpProps>(() => import("./quantitative/QuantitativeSectionSp"));
const QuantitativeSectionPc = dynamic<QuantitativeSectionPcProps>(() => import("./quantitative/QuantitativeSectionPc"));
const DetailQAList = dynamic<DetailQAListProps>(() => import("./DetailQAList"));
const DarkBackground = dynamic<Record<string, never>>(() => import("../layout/DarkBackground").then((m) => m.DarkBackground), {
  ssr: false,
  loading: LoadingDarkBg,
});
const ZendeskHelpButton = dynamic<ZendeskHelpButtonProps>(() => import("./ZendeskHelpButton"));
const CourseListView = dynamic<CourseListViewProps>(() => import("components/front/project/CourseListView"), { ssr: false });
const CommentSection = dynamic<CommentSectionProps>(() => import("./comment/CommentSection"), { ssr: false });
const FgFireworkFrame = dynamic(() => import("../aniale/ForegroundFireworkFrame"), { ssr: false });
const BgFireworkFrame = dynamic<Record<string, never>>(() => import("../../front/aniale/BackgroundFireworkFrame"), { ssr: false });
const Popup = dynamic<PopupProps>(() => import("./Popup").then((m) => m.Popup));
const RequestContactButton = dynamic<RequestContactButtonProps>(
  () => import("./contact/RequestContactButton").then((m) => m.RequestContactButton),
  {
    ssr: false,
  }
);
const StartCountDownArea = dynamic<StartCountDownAreaProps>(() => import("components/front/project/StartCountDownArea"), { ssr: false });
const StoneMonumentSection = dynamic<StoneMonumentSectionProps>(() => import("components/front/project/StoneMonumentSection"));
const CorporateLogoSection = dynamic<CorporateLogoSectionProps>(() => import("components/front/project/CorporateLogoSection"));
const RelatedLinkSection = dynamic<RelatedLinkSectionProps>(() => import("components/front/project/RelatedLinkSection"));
const CourseListSection = dynamic<CourseListSectionProps>(() => import("components/front/project/content/CourseListSection"));
const Footer = dynamic<FooterProps>(() => import("components/front/layout/Footer"));
const BackToTop = dynamic<BackToTopProps>(() => import("components/front/layout/BackToTop"));
const GeneralFoot = dynamic<GeneralFootProps>(() => import("components/front/layout/GeneralFoot").then((m) => m.GeneralFoot));
const HometownTaxAnnotation = dynamic<HometownTaxAnnotationProps>(() => import("./HometownTaxAnnotation"));
const AmagamiText = dynamic<AmagamiTextProps>(() => import("./AmagamiText"));
const KaleidoShoppingText = dynamic<KaleidoShoppingTextProps>(() => import("./KaleidoShoppingText"));
const ProjectActivitySection = dynamic<ProjectActivitySectionProps>(
  () => import("components/front/project/activity/ProjectActivitySection")
);
const HometownTaxVideoPopup = dynamic<HometownTaxVideoPopupProps>(() => import("components/front/project/banner/HometownTaxVideoPopup"));

type Props = {
  slug: string;
  project: ExhibitionProject;
  rewardCourses: RewardCourse[];
  donationCourse: DonationCourse | null | undefined;
  aniale: Aniale | null | undefined;
  summary?: ProjectTotalization;
  monumentNames?: MonumentName[];
  isPreviewMode?: boolean;
};

export const ProjectPageContents: FC<Props> = ({
  project,
  slug,
  rewardCourses,
  donationCourse,
  aniale,
  summary,
  monumentNames,
  isPreviewMode = false,
}: Props) => {
  const router = useRouter();
  const member = useFrontAuthContext();
  const loginStatus = calcLoginStatus(member);
  const goalAmount = useAmount(project.goal_amount ?? undefined);
  const supportAmount = useAmount(summary?.support_amount ?? undefined);
  const supportAmountPool = useSupportAmountPool(summary);
  const deviceType = useDeviceType();
  const memberTippingCommentHelper = useMemberTippingComment(project.code);
  const remaining = useRemainingDays(project.support_end_at);
  const rating = useRating(project.goal_amount, summary?.support_amount);
  const { isAboutHometownTax, isCorpHometownTax, isHometownTax, isShopping } = useServiceType(project.service_type);
  const [wrapFooterElement, detailContentLinkElement, isScrolledByBottom] = useScrollLimitation();
  const showsStoneMonument = useMemo(() => project.monument_flag, [project]);
  const shownCourseList = useCourseListSwitch(router);
  const showsSupportAmount = useShowsSupportAmount(summary?.support_amount, project.support_amount);
  const returnIntroLabel = useReturnIntroLabel(isAboutHometownTax, isHometownTax);
  const storyTitle = useMemo(
    () => project.content.story_title || (isAboutHometownTax ? "アニメの紹介" : "ストーリー"),
    [project.content.story_title, isAboutHometownTax]
  );
  const [videoOpened, openVideo, closeVideo] = useActivation(false);
  useSmoothAnchor("web_name");
  useContentAccordion();
  //content.return_intro内のボタンをクリックしたときにuseRewardCourseImageSwiper.tsを動かすフラグ
  const [isShownByClick, setIsShownByClick] = useState(false);
  useEffect(() => {
    const buttons = document.querySelectorAll(".content-return-button");
    const clicker = () => {
      setIsShownByClick(true);
    };
    buttons.forEach(button => {
      button.addEventListener('click', clicker);
    });
    return () => {
      buttons.forEach(button => {
        button.removeEventListener('click', clicker);
      });
    };
  }, []);
  return (
    <>
      <Header />
      <ProjectContentStyle />
      <DarkBackground />
      <StyledMain>
        <AnchorTarget id="page_top" />
        <RootSection>
          <MainSideArea $shownCourseList={shownCourseList}>
            <StartCountDownArea supportStartAt={project.support_start_at} isShopping={isShopping} />
            <StyledTitleSection projectCode={project.code} serviceType={project.service_type} title={project.content.title} summary={project.content.summary} />
            <MainImageSection content={project.content} />
            <QuantitativeSectionSp
              slug={project.code}
              title={project.content.title}
              twitterHashtags={[project.content.twitter_hashtag1, project.content.twitter_hashtag2]}
              supportAmount={supportAmount}
              supportAmountPool={supportAmountPool}
              goalAmount={goalAmount}
              rating={rating}
              summary={summary}
              remaining={remaining}
              isHometownTax={isHometownTax}
              isCorpHometownTax={isCorpHometownTax}
              isAboutHometownTax={isAboutHometownTax}
              isShopping={isShopping}
              showsSupportAmount={showsSupportAmount}
              onOpenVideo={openVideo}
              banners={project.content.banners}
            />
            <ProjectActivitySection activities={project.activities} />
            {project.code == "amagami" && (
              <Section>
                <SsHeadline label="銚子市アニメツーリズム協議会" color="white" />
                <StyledAmagamiText />
              </Section>
            )}
            {project.code == "kaleido_goods_true" && (
              <Section>
                <SsHeadline label="事前通販について" color="white" />
                <StyledKaleidoShoppingText />
              </Section>
            )}
            {!isShopping && (
              <Section>
                <SsHeadline label="プロジェクトについて" color="white" />
                <StyledHometownTaxAnnotation isHometownTax={isHometownTax} />
                <SectionContent content={project.content.greeting} usesSeeMore />
              </Section>
            )}
            {rewardCourses.length > 0 && project.content.course_list_design !== "not_display" && (
              <CourseListSection rewardCourses={rewardCourses} design={project.content.course_list_design} project={project} isShopping={isShopping} slug={slug} />
            )}
            {project.content.return_intro && (
              <Section>
                <SsHeadline label={returnIntroLabel} color="white" />
                <SectionContent content={project.content.return_intro} usesSeeMore />
              </Section>
            )}
            {project.content.novelty_intro && (
              <Section>
                <SsHeadline label="付属品紹介" color="white" />
                <SectionContent content={project.content.novelty_intro} usesSeeMore />
              </Section>
            )}
            {project.content.return_chart && (
              <Section>
                <SsHeadline label={project.content.return_chart_title || "コースリターン早見表"} color="white" />
                <SectionContent content={project.content.return_chart} usesSeeMore />
              </Section>
            )}
            {!!project.content.sanctuary_title && !!project.content.sanctuary_text && (
              <Section>
                <SsHeadline label={project.content.sanctuary_title} color="white" />
                <SectionContent content={project.content.sanctuary_text} usesSeeMore />
              </Section>
            )}
            <Section className="detail_qa detail_whiteBox">
              <div className="box_title box_title--black">
                <div className="box_title_bg" />
                <h3 className="box_title_text" style={{ zIndex: 2 }}>
                  Q&A
                </h3>
              </div>
              <DetailQAList project={project} isShopping={isShopping}/>
              {!loginStatus.isLoading && <RequestContactButton projectSlug={project.code} member={member} />}
            </Section>
            {aniale !== undefined && !isShopping && (
              <CommentSection
                enabledAniale={!!aniale}
                project={project}
                memberTippingCommentHelper={memberTippingCommentHelper}
              />
            )}
            {!!project.content.story && (
              <Section>
                <SsHeadline label={storyTitle} color="white" />
                <SectionContent content={project.content.story} />
              </Section>
            )}
            {isAboutHometownTax && (
              <Section>
                <SsHeadline label="自治体の紹介" color="white" />
                <SectionContent content={project.content.municipality_about} />
              </Section>
            )}
            {project.content.credit && (
              <Section>
                <SsHeadline label={project.content.credit_title || "スタッフ・キャスト"} color="white" />
                <SectionContent content={project.content.credit} />
              </Section>
            )}
            <Section>
              <RelatedLinkSection content={project.content} />
            </Section>
            {showsStoneMonument && monumentNames && <StoneMonumentSection slug={slug} monumentNames={monumentNames} />}
            {isCorpHometownTax && project.content.corporate_logo && (
              <CorporateLogoSection corporateLogo={project.content.corporate_logo} />
            )}
            {!!project.content.copyright_notation && (
              <Section style={{ marginBottom: "20px" }}>
                <SectionContent content={project.content.copyright_notation} />
              </Section>
            )}
          </MainSideArea>
          <RightSideArea isScrolledByBottom={isScrolledByBottom} shownCourseList={shownCourseList} ref={detailContentLinkElement}>
            <QuantitativeSectionPc
              slug={slug}
              title={project.content.title}
              twitterHashtags={[project.content.twitter_hashtag1, project.content.twitter_hashtag2]}
              supportAmount={supportAmount}
              supportAmountPool={supportAmountPool}
              rating={rating}
              goalAmount={goalAmount}
              summary={summary}
              remaining={remaining}
              isPreviewMode={isPreviewMode}
              isShopping={isShopping}
              showsSupportAmount={showsSupportAmount}
            />
            <StyledBannerArea>
              {isHometownTax && <AnimefurusatoLpBanner />}
              {isHometownTax && <SimulatorBanner />}
              {isHometownTax && <HometownTaxVideoBanner onClick={openVideo} />}
              {isCorpHometownTax && <CorpfurusatoBanner /> }
              {!isAboutHometownTax && project.content.banners.map((banner, index) => <ProjectBanner banner={banner} key={index} />)}
            </StyledBannerArea>
          </RightSideArea>
        </RootSection>
      </StyledMain>
      <section className="detail_visual sp">
        <div className="visual_content_area">
          <LinkButtonSp slug={slug} isPreviewMode={isPreviewMode} isShopping={isShopping}/>
          {deviceType.sp && <ZendeskHelpButton placeHolder={<SpBottomLeftCorner projectCode={project.code} />} />}
        </div>
      </section>
      {rewardCourses && (
        <CourseListView
          project={project}
          courses={rewardCourses}
          donationCourse={donationCourse}
          aniale={aniale}
          isPreviewMode={isPreviewMode}
          isShopping={isShopping}
          onCloseHandle={async () => {
            await router.push({
              pathname: `${isPreviewMode ? "/admin/preview" : ""}/project/${slug}`,
            });
          }}
          afterTippingSuccess={async () => {
            triggerFireworks(deviceType).catch(console.error);
          }}
          memberTippingCommentHelper={memberTippingCommentHelper}
          shown={shownCourseList}
          isShownByClick={isShownByClick}
        />
      )}
      <StyledBackToTop />
      <BgFireworkFrame />
      <FgFireworkFrame />
      <Popup slug={slug} />
      {isHometownTax && <HometownTaxVideoPopup opened={videoOpened} onClose={closeVideo} />}
      {deviceType.pc && <ZendeskHelpButton placeHolder={null} />}
      <div ref={wrapFooterElement}>
        <Footer existsStickyContent={true} />
      </div>
      <GeneralFoot />
    </>
  );
};

const triggerFireworks = async (deviceType: { pc: boolean; sp: boolean }) => {
  try {
    await import("lib/sketch/fireworks").then((m) => m.startFireworks(FIREWORK_FG_FRAME_ID, deviceType, 5));
  } catch (e) {
    Sentry.captureException(e);
  }
};

const StyledMain = styled.main``;
const RootSection = styled.section`
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  padding-right: 0;
  padding-left: 0;
  padding-bottom: 80px;
  @media screen and (max-width: 960px) {
    width: 100%;
  }
  @media screen and (min-width: 961px) and (max-width: 1280px) {
    width: 960px;
  }
  @media screen and (min-width: 1281px) and (max-width: 1920px) {
    width: 1080px;
  }
  @media screen and (min-width: 1921px) {
    width: 1280px;
  }
`;
const StyledTitleSection = styled(TitleSection)`
  @media screen and (max-width: 960px) {
    margin-bottom: 20px;
  }
  @media screen and (min-width: 961px) {
    margin-bottom: 28px;
  }
`;
const StyledBannerArea = styled(BannerArea)`
  margin: 20px auto;
  width: 310px;
`;
const StyledBackToTop = styled(BackToTop)`
  && {
    bottom: 90px;
  }
`;
const StyledHometownTaxAnnotation = styled(HometownTaxAnnotation)`
  margin: 20px 0;
`;
const StyledAmagamiText = styled(AmagamiText)`
  margin: 20px 0;
`;
const StyledKaleidoShoppingText = styled(KaleidoShoppingText)`
  margin: 20px 0;
`;
const MainSideArea = styled.div<{ $shownCourseList: boolean }>`
  transition: 0.4s;
  transform: ${({ $shownCourseList }) => ($shownCourseList ? "translateX(-100%)" : "translateX(0)")};
  opacity: ${({ $shownCourseList }) => ($shownCourseList ? "0" : "1")};
  overflow: hidden;
  @media screen and (max-width: 960px) {
    width: 100%;
    padding-top: 20px;
  }
  @media screen and (min-width: 961px) {
    padding-top: 40px;
  }
  @media screen and (min-width: 961px) and (max-width: 1280px) {
    width: 615px;
  }
  @media screen and (min-width: 1281px) and (max-width: 1920px) {
    width: 700px;
  }
  @media screen and (min-width: 1921px) {
    width: 900px;
  }

  & section {
    width: 100%;
  }
`;
const RightSideArea = styled.div<{ isScrolledByBottom: boolean; shownCourseList: boolean }>`
  position: ${({ isScrolledByBottom }) => (isScrolledByBottom ? "absolute" : "fixed")};
  top: ${({ isScrolledByBottom }) => (isScrolledByBottom ? "auto" : "148px")};
  bottom: ${({ isScrolledByBottom }) => (isScrolledByBottom ? "calc(60px + 284px)" : "unset")};
  transition: 0.4s;
  opacity: ${({ shownCourseList }) => (shownCourseList ? "0" : "1")};
  @media screen and (max-width: 960px) {
    display: none;
  }
  @media screen and (min-width: 961px) and (max-width: 1280px) {
    width: 305px;
    left: ${({ shownCourseList }) => (shownCourseList ? "-100%" : "calc(50% + 175px)")};
  }
  @media screen and (min-width: 1281px) and (max-width: 1920px) {
    width: 340px;
    left: ${({ shownCourseList }) => (shownCourseList ? "-100%" : "calc(50% + 200px)")};
  }
  @media screen and (min-width: 1921px) {
    width: 340px;
    left: ${({ shownCourseList }) => (shownCourseList ? "-100%" : "calc(50% + 300px)")};
  }
`;
